export type EegStartupMilestone =
  | "routeShellPainted"
  | "viewerChunkRequested"
  | "viewerChunkLoaded"
  | "viewerComponentMounted"
  | "manifestRequested"
  | "manifestReceived"
  | "firstCanvasPaint"
  | "firstCanvasRender"
  | "playbackControlsInteractive"
  | "firstTileTransferObserved"
  | "viewerRoutePrefetchStarted"
  | "viewerRoutePrefetchCompleted";

export type EegStartupTaskWindow = {
  startMs: number;
  durationMs: number;
  name: string;
  entryType: "longtask" | "long-animation-frame" | "named-block";
};

export type EegStartupNamedBlock = {
  name: string;
  startMs: number;
  durationMs: number;
};

export type EegStartupTelemetrySnapshot = {
  origin: string;
  pathname: string;
  startedAtMs: number;
  milestones: Partial<Record<EegStartupMilestone, number>>;
  timeToPlayableMs: number | null;
  startupTaskTotalMs: number | null;
  startupTaskMaxMs: number | null;
  startupTaskCount: number;
  startupTaskWindows: EegStartupTaskWindow[];
  /** Sprint 4C-001: explicit named startup work blocks. */
  startupNamedBlocks: EegStartupNamedBlock[];
};

declare global {
  interface Window {
    __NERVEDX_EEG_STARTUP__?: EegStartupTelemetrySnapshot;
  }
}

const MAX_STARTUP_TASK_WINDOWS = 8;

let startedAtMs =
  typeof performance !== "undefined" ? performance.now() : Date.now();

const milestones: Partial<Record<EegStartupMilestone, number>> = {};
const milestoneListeners = new Set<(milestone: EegStartupMilestone) => void>();

let startupTaskTotalMs: number | null = null;
let startupTaskMaxMs: number | null = null;
let startupTaskCount = 0;
let startupTaskWindows: EegStartupTaskWindow[] = [];
let startupNamedBlocks: EegStartupNamedBlock[] = [];
let startupTaskAttributionActive = false;
let longTaskObserver: PerformanceObserver | null = null;
let longAnimationFrameObserver: PerformanceObserver | null = null;

const MAX_NAMED_BLOCKS = 12;

function relativeMs(absoluteMs?: number): number {
  const now =
    absoluteMs ??
    (typeof performance !== "undefined" ? performance.now() : Date.now());
  return Math.max(0, Math.round(now - startedAtMs));
}

function publishSnapshot(): void {
  if (typeof window === "undefined") return;

  const snapshot: EegStartupTelemetrySnapshot = {
    origin: window.location.origin,
    pathname: window.location.pathname,
    startedAtMs,
    milestones: { ...milestones },
    timeToPlayableMs:
      milestones.playbackControlsInteractive ??
      milestones.firstCanvasPaint ??
      milestones.manifestReceived ??
      null,
    startupTaskTotalMs,
    startupTaskMaxMs,
    startupTaskCount,
    startupTaskWindows: [...startupTaskWindows],
    startupNamedBlocks: [...startupNamedBlocks],
  };

  window.__NERVEDX_EEG_STARTUP__ = snapshot;
}

function recordStartupTask(
  entryType: EegStartupTaskWindow["entryType"],
  startTime: number,
  duration: number,
  name: string,
): void {
  if (!startupTaskAttributionActive) return;
  if (!Number.isFinite(duration) || duration <= 0) return;

  const window: EegStartupTaskWindow = {
    startMs: relativeMs(startTime),
    durationMs: Math.round(duration),
    name: name || entryType,
    entryType,
  };

  startupTaskCount += 1;
  startupTaskTotalMs = (startupTaskTotalMs ?? 0) + window.durationMs;
  startupTaskMaxMs = Math.max(startupTaskMaxMs ?? 0, window.durationMs);

  startupTaskWindows = [...startupTaskWindows, window]
    .sort((left, right) => right.durationMs - left.durationMs)
    .slice(0, MAX_STARTUP_TASK_WINDOWS);

  publishSnapshot();
}

/**
 * Measure a named startup work block with performance.mark/measure and
 * include it in the Lighthouse summary artifact (Sprint 4C-001).
 */
export function measureEegStartupBlock<T>(name: string, run: () => T): T {
  const markStart = `eeg-startup:${name}:start`;
  const markEnd = `eeg-startup:${name}:end`;
  const measureName = `eeg-startup:${name}`;
  const started =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  if (typeof performance !== "undefined" && typeof performance.mark === "function") {
    try {
      performance.mark(markStart);
    } catch {
      /* ignore duplicate mark races */
    }
  }

  try {
    return run();
  } finally {
    const ended =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    const durationMs = Math.max(0, Math.round(ended - started));

    if (
      typeof performance !== "undefined" &&
      typeof performance.mark === "function"
    ) {
      try {
        performance.mark(markEnd);
        performance.measure(measureName, markStart, markEnd);
      } catch {
        /* ignore measure failures in restricted environments */
      }
    }

    const block: EegStartupNamedBlock = {
      name,
      startMs: relativeMs(started),
      durationMs,
    };
    startupNamedBlocks = [...startupNamedBlocks, block]
      .sort((left, right) => right.durationMs - left.durationMs)
      .slice(0, MAX_NAMED_BLOCKS);

    if (startupTaskAttributionActive && durationMs >= 16) {
      recordStartupTask("named-block", started, durationMs, name);
    } else {
      publishSnapshot();
    }
  }
}

function disconnectStartupTaskObservers(): void {
  longTaskObserver?.disconnect();
  longAnimationFrameObserver?.disconnect();
  longTaskObserver = null;
  longAnimationFrameObserver = null;
}

function finalizeEegStartupTaskAttribution(): void {
  if (!startupTaskAttributionActive) return;
  startupTaskAttributionActive = false;
  disconnectStartupTaskObservers();
  if (startupTaskTotalMs == null) {
    startupTaskTotalMs = 0;
  }
  if (startupTaskMaxMs == null) {
    startupTaskMaxMs = 0;
  }
  publishSnapshot();
}

function beginEegStartupTaskAttribution(): void {
  if (typeof PerformanceObserver === "undefined") {
    startupTaskAttributionActive = true;
    startupTaskTotalMs = 0;
    startupTaskMaxMs = 0;
    publishSnapshot();
    return;
  }

  if (startupTaskAttributionActive) return;
  startupTaskAttributionActive = true;
  startupTaskTotalMs = 0;
  startupTaskMaxMs = 0;
  startupTaskCount = 0;
  startupTaskWindows = [];
  disconnectStartupTaskObservers();

  try {
    longTaskObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        recordStartupTask(
          "longtask",
          entry.startTime,
          entry.duration,
          entry.name || "longtask",
        );
      }
    });
    longTaskObserver.observe({
      type: "longtask",
      buffered: true,
    } as PerformanceObserverInit);
  } catch {
    longTaskObserver = null;
  }

  try {
    longAnimationFrameObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const duration =
          typeof (entry as PerformanceEntry & { duration?: number }).duration ===
          "number"
            ? entry.duration
            : 0;
        // LOAF entries can be shorter than classic longtasks; keep only
        // clinically-relevant blocking windows for startup attribution.
        if (duration < 50) continue;
        recordStartupTask(
          "long-animation-frame",
          entry.startTime,
          duration,
          entry.name || "long-animation-frame",
        );
      }
    });
    longAnimationFrameObserver.observe({
      type: "long-animation-frame",
      buffered: true,
    } as PerformanceObserverInit);
  } catch {
    longAnimationFrameObserver = null;
  }

  publishSnapshot();
}

export function resetEegStartupTelemetry(): void {
  finalizeEegStartupTaskAttribution();
  startedAtMs =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  for (const key of Object.keys(milestones) as EegStartupMilestone[]) {
    delete milestones[key];
  }
  startupTaskTotalMs = null;
  startupTaskMaxMs = null;
  startupTaskCount = 0;
  startupTaskWindows = [];
  startupNamedBlocks = [];
  publishSnapshot();
}

export function markEegStartup(milestone: EegStartupMilestone): void {
  if (milestones[milestone] != null) return;
  milestones[milestone] = relativeMs();

  if (milestone === "viewerComponentMounted") {
    beginEegStartupTaskAttribution();
  }
  if (milestone === "playbackControlsInteractive") {
    finalizeEegStartupTaskAttribution();
  }

  publishSnapshot();
  for (const listener of milestoneListeners) {
    listener(milestone);
  }
}

export function subscribeEegStartupMilestone(
  listener: (milestone: EegStartupMilestone) => void,
): () => void {
  milestoneListeners.add(listener);
  return () => {
    milestoneListeners.delete(listener);
  };
}

export function getEegStartupTelemetry(): EegStartupTelemetrySnapshot {
  publishSnapshot();
  if (typeof window !== "undefined" && window.__NERVEDX_EEG_STARTUP__) {
    return window.__NERVEDX_EEG_STARTUP__;
  }
  return {
    origin: typeof window !== "undefined" ? window.location.origin : "",
    pathname: typeof window !== "undefined" ? window.location.pathname : "",
    startedAtMs,
    milestones: { ...milestones },
    timeToPlayableMs: null,
    startupTaskTotalMs,
    startupTaskMaxMs,
    startupTaskCount,
    startupTaskWindows: [...startupTaskWindows],
    startupNamedBlocks: [...startupNamedBlocks],
  };
}

/** Structured Lighthouse / soak evidence payload (EEG-MKT20-01-002). */
export function buildEegStartupLighthouseSummary(
  snapshot: EegStartupTelemetrySnapshot = getEegStartupTelemetry(),
): {
  schemaVersion: string;
  timeToPlayableMs: number | null;
  milestones: {
    firstCanvasPaint: number | null;
    viewerChunkLoaded: number | null;
    firstTileTransferObserved: number | null;
    playbackControlsInteractive: number | null;
  };
  longTasks: {
    totalMs: number | null;
    maxMs: number | null;
    count: number;
  };
  pathname: string;
} {
  return {
    schemaVersion: "eeg-startup-lighthouse-v1.0.0",
    timeToPlayableMs: snapshot.timeToPlayableMs,
    milestones: {
      firstCanvasPaint: snapshot.milestones.firstCanvasPaint ?? null,
      viewerChunkLoaded: snapshot.milestones.viewerChunkLoaded ?? null,
      firstTileTransferObserved:
        snapshot.milestones.firstTileTransferObserved ?? null,
      playbackControlsInteractive:
        snapshot.milestones.playbackControlsInteractive ?? null,
    },
    longTasks: {
      totalMs: snapshot.startupTaskTotalMs,
      maxMs: snapshot.startupTaskMaxMs,
      count: snapshot.startupTaskCount,
    },
    pathname: snapshot.pathname,
  };
}
